import React, { FC, useEffect } from 'react';
import { RouteComponentProps, useHistory, withRouter } from 'react-router';
import { useButton, useDeviceInfo, useI18n, useLicenses, useLogger, useSelector } from '~/Hooks';
import DemoIntro from '~Scenes/DemoIntro';
import { CompanyModules } from './types';
import { BlueButton, Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import ExpressIconSrc from '~Assets/icons/product_express-checkout.svg';
import KDSIconSrc from '~Assets/icons/product_kitchen-display.svg';
import ProductORDSrc from '~Assets/icons/product_order-ready-display.svg';
import QSRIconSrc from '~Assets/icons/pos.svg';
import MultiQSRIconSrc from '~Assets/icons/devices.svg';
import TSRIconSrc from '~Assets/icons/tsr.svg';
import HandheldIconSrc from '~Assets/icons/handheld.svg';
import InventoryIcon from '~Assets/icons/boxes_stacked_gray.svg';
import LoginModal from '~Scenes/Tables/Components/LoginModal';
import bLogo from '~Assets/Logos/b_logo_black.svg';
import Mode from './Components/Mode';
import ModeSkeleton from './Components/ModeSkeleton';
import POSBackgroundSrc from '~Assets/Images/Gateway/pos.webp';
import TSRBackgroundSrc from '~Assets/Images/Gateway/tsr.webp';
import ExpressBackgroundSrc from '~Assets/Images/Gateway/express.webp';
import CDSVerticalBackgroundSrc from '~Assets/Images/Gateway/customer-display-vertical.webp';
import CDSHorizontalBackgroundSrc from '~Assets/Images/Gateway/customer-display-horizontal.webp';
import KDSBackgroundSrc from '~Assets/Images/Gateway/kds.webp';
import HandheldBackgroundSrc from '~Assets/Images/Gateway/handheld.webp';
import {
  GradientBlur,
  Logo,
  LogoContainer,
  ModesWrapper,
  NoLicensesWrapper,
  ScrollWrapper,
  Wrapper,
} from './styles';
import Icon from '~ComponentsImproved/Icon';
import LicenseIcon from '~Assets/icons/license.svg';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import useModal from '~ComponentsImproved/Modal/useModal';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import ClaimDevice from '~Components/ClaimDevice';
import NewsAndUpdates from './Components/NewsAndUpdates';
import { useWindowSize } from '~/Hooks';
import ReleasesButton from './Components/ReleasesButton';

type Props = RouteComponentProps;

const Gateway: FC<Props> = (props) => {
  const { i18n } = useI18n();
  const licenses = useLicenses();
  const deviceInfo = useDeviceInfo();
  const logger = useLogger('gateway');
  const history = useHistory();
  const windowSize = useWindowSize();

  const changeDeviceModal = useModal();

  const demoMode = useSelector((state) => state.demoMode);
  const staffId = useSelector((state) => state.staff?.id);
  const venueName = useSelector((state) => state.venueName);
  const companyFlags = useSelector((state) => state.companyFlags);
  const [, , venueId] = props.location.pathname.split('/');

  const companyModules: CompanyModules = {
    POS: { active: licenses.hasQSR, claimable: licenses.isQSRClaimable, path: 'pos' },
    tables: { active: licenses.hasTSR, claimable: licenses.isTSRClaimable, path: 'tables' },
    appOrders: { active: licenses.hasApp, claimable: false, path: 'orders' },
    KDS: { active: licenses.hasKDS, claimable: licenses.isKDSClaimable, path: 'kitchen-display' },
    CDS: {
      active: licenses.hasCustomerDisplay,
      claimable: licenses.isCustomerDisplayClaimable,
      path: 'customer-display',
    },
    express: {
      active: licenses.hasEPOS,
      claimable: licenses.isEPOSClaimable,
      path: 'self-service-kiosks',
    },
    inventory: { active: licenses.hasInventory, claimable: false, path: 'inventory' },
  };

  const activeModules = Object.keys(companyModules).filter(
    (cm) => companyModules[cm as keyof CompanyModules].active,
  );

  const changeDeviceButton = useButton({
    onClick: () => {
      logger.info('Change device modal opened in Gateway');
      changeDeviceModal.toggle();
    },
  });

  // Animation delay counter for staggered entrance
  let animationIndex = 0;
  const getAnimationDelay = () => {
    const delay = animationIndex * 50;
    animationIndex++;
    return delay;
  };

  /**
   * Redirect to handy flow if it's a handheld terminal
   */
  useEffect(() => {
    logger.info('checking gateway');

    if (!deviceInfo) {
      return;
    }

    logger.info('Model: ' + deviceInfo.model);
    logger.info('Is handheld terminal: ' + deviceInfo.isHandheldTerminal);

    if (deviceInfo.isHandheldTerminal) {
      logger.info('pushing to handy flow');
      history.push(`/venue/${venueId}/handy`);
    }
  }, [deviceInfo]);

  const noLicensesAvailable =
    !licenses.loading && companyFlags.useNewLicenses && activeModules.length === 0;

  const showReleasesInModal = windowSize.width < 944;

  if (demoMode) {
    return <DemoIntro />;
  }

  return (
    <>
      <Wrapper>
        <GradientBlur aria-hidden="true" />
        <ScrollWrapper>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginTop="medium"
            fluid
            style={{ textAlign: noLicensesAvailable ? 'center' : undefined }}>
            <Box display="flex" flexDirection="column" gap="xs" fluid>
              <Heading level={1} color={'var(--colorWhite)'}>
                {venueName ?? i18n('Menu.Home')}
              </Heading>
              <Heading level={4} color={'var(--colorBlueGray200)'}>
                {i18n('General.ChooseMode')}
              </Heading>
            </Box>

            {showReleasesInModal ? (
              <ReleasesButton />
            ) : (
              !noLicensesAvailable && (
                <LogoContainer>
                  <Logo src={bLogo} alt="Baemingo Logo" />
                </LogoContainer>
              )
            )}
          </Box>

          <Box display="flex" justifyContent="space-between" gap="medium">
            {companyFlags.useNewLicenses && licenses.loading ? (
              <ModesWrapper display="flex" flexDirection="column" gap="small" marginTop="medium">
                <Box display="flex" gap="small">
                  <ModeSkeleton animationDelay={0} />
                  <ModeSkeleton animationDelay={50} />
                </Box>
                <ModeSkeleton animationDelay={100} />
                <ModeSkeleton animationDelay={150} />
              </ModesWrapper>
            ) : noLicensesAvailable ? (
              <NoLicensesWrapper
                display="flex"
                alignItems="center"
                justifyContent="center"
                marginTop="3xl">
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap="xs"
                  justifyContent="center">
                  <Icon
                    height="64px"
                    width="64px"
                    src={LicenseIcon}
                    color="var(--colorBlueGray200)"
                  />

                  <Heading level={2} color="var(--colorWhite)">
                    {i18n('Gateway.NoLicensesAvailable')}
                  </Heading>
                  <Paragraph
                    style={{ textAlign: 'center' }}
                    level={4}
                    color="var(--colorBlueGray200)">
                    {i18n('Gateway.NoLicensesAvailable.Desc')}
                  </Paragraph>

                  <BlueButton marginTop="small" {...changeDeviceButton}>
                    {i18n('ClaimDevice.ChangeDevice')}
                  </BlueButton>
                </Box>
              </NoLicensesWrapper>
            ) : (
              <ModesWrapper display="flex" flexDirection="column" gap="small" marginTop="medium">
                {(companyModules.POS.active || companyModules.POS.claimable) && (
                  <Box display="flex" gap="small">
                    <Mode
                      to={`/venue/${venueId}/pos`}
                      text={i18n('Gateway.PointOfSale')}
                      isClaimable={companyModules.POS.claimable}
                      iconSrc={QSRIconSrc}
                      backgroundImageSrc={POSBackgroundSrc}
                      animationDelay={getAnimationDelay()}
                    />
                    <Mode
                      to={`/venue/${venueId}/multi-pos`}
                      text={i18n('Gateway.PointOfSaleMulti')}
                      isClaimable={companyModules.POS.claimable}
                      iconSrc={MultiQSRIconSrc}
                      backgroundImageSrc={POSBackgroundSrc}
                      animationDelay={getAnimationDelay()}
                    />
                  </Box>
                )}
                {(companyModules.tables.active || companyModules.tables.claimable) && (
                  <Mode
                    to={`/venue/${venueId}/tables`}
                    text={i18n('Gateway.TableManagement')}
                    isClaimable={companyModules.tables.claimable}
                    iconSrc={TSRIconSrc}
                    backgroundImageSrc={TSRBackgroundSrc}
                    animationDelay={getAnimationDelay()}
                  />
                )}
                {(companyModules.KDS.active || companyModules.KDS.claimable) && (
                  <Mode
                    to={`/venue/${venueId}/evented-kitchen-display`}
                    text={i18n('Gateway.KitchenDisplay')}
                    isClaimable={companyModules.KDS.claimable}
                    iconSrc={KDSIconSrc}
                    backgroundImageSrc={KDSBackgroundSrc}
                    animationDelay={getAnimationDelay()}
                  />
                )}
                {(companyModules.CDS.active || companyModules.CDS.claimable) && (
                  <Box display="flex" gap="small">
                    <Mode
                      to={`/venue/${venueId}/customer-display`}
                      text={i18n('Gateway.CustomerDisplay')}
                      isClaimable={companyModules.CDS.claimable}
                      iconSrc={ProductORDSrc}
                      backgroundImageSrc={CDSVerticalBackgroundSrc}
                      animationDelay={getAnimationDelay()}
                    />
                    <Mode
                      to={`/venue/${venueId}/customer-display-responsive`}
                      text={i18n('Gateway.CustomerDisplayLarge')}
                      isClaimable={companyModules.CDS.claimable}
                      iconSrc={ProductORDSrc}
                      backgroundImageSrc={CDSHorizontalBackgroundSrc}
                      animationDelay={getAnimationDelay()}
                    />
                  </Box>
                )}
                {(companyModules.express.active || companyModules.express.claimable) && (
                  <Mode
                    to={`/venue/${venueId}/self-service-kiosks`}
                    text={i18n('Gateway.SelfServiceKiosk')}
                    isClaimable={companyModules.express.claimable}
                    iconSrc={ExpressIconSrc}
                    backgroundImageSrc={ExpressBackgroundSrc}
                    animationDelay={getAnimationDelay()}
                  />
                )}
                {(companyModules.inventory.active || companyModules.inventory.claimable) && (
                  <Mode
                    to={`/venue/${venueId}/inventory`}
                    text={i18n('Gateway.Inventory')}
                    isClaimable={companyModules.inventory.claimable}
                    iconSrc={InventoryIcon}
                    animationDelay={getAnimationDelay()}
                  />
                )}
                {(deviceInfo?.isHandheldTerminal || window.__DEV__) && (
                  <Mode
                    to={`/venue/${venueId}/handy`}
                    text={i18n('Handy.Title')}
                    isClaimable={false}
                    iconSrc={HandheldIconSrc}
                    backgroundImageSrc={HandheldBackgroundSrc}
                    animationDelay={getAnimationDelay()}
                  />
                )}
              </ModesWrapper>
            )}

            {!noLicensesAvailable && !showReleasesInModal && <NewsAndUpdates />}
          </Box>
        </ScrollWrapper>
      </Wrapper>

      {!staffId && <LoginModal />}

      <Modal isShowing={changeDeviceModal.isShowing} hide={() => changeDeviceModal.toggle()}>
        <ModalWrapper>
          <ClaimDevice
            isChangeOfDevice
            onComplete={() => {
              logger.info('Device changed in Gateway');
              changeDeviceModal.toggle();
            }}
          />
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default withRouter(Gateway);
